import { z } from "zod";
import type { EmpSharedPost } from "./feed";

/**
 * Messages (Phase 8, epic #1204): private conversations between colleagues.
 *
 * Four rules shape this file, and all four are enforced on the server rather
 * than merely described here:
 *
 * 1. **Only participants can read a conversation.** Not Managers, not
 *    Administrators (roadmap decision 6). Anyone else gets 404 rather than 403
 *    — a 403 confirms the conversation exists, which already discloses that two
 *    named people are talking.
 * 2. **A report carries its own copy of the message it reports**, so a reviewer
 *    never needs access to the thread (decision 7). That copy includes media,
 *    because a reviewer has to be able to hear a reported voice note.
 * 3. **Messages are deleted after `messageRetentionDays`** — the row *and* the
 *    blobs behind it. Rows deleted with blobs left behind would leave private
 *    photos and voice notes in storage after the product said they were gone.
 * 4. **Nothing here is end-to-end encrypted**, and the notice says so. Rules 2
 *    and 3 both require the server to read and delete content, so claiming
 *    secrecy we don't provide would be the dishonest option.
 */

// ──────────────────────────── Conversations ────────────────────────────

/**
 * `direct` is two people and ships first; `group` is modelled from the start so
 * it arrives later without a migration. A direct conversation between a given
 * pair is unique — opening one twice reopens the same thread rather than
 * splitting the history in two.
 */
export const CONVERSATION_KINDS = ["direct", "group"] as const;
export type EmpConversationKind = (typeof CONVERSATION_KINDS)[number];

export const MESSAGE_BODY_MAX = 4000;
export const GROUP_TITLE_MAX = 80;
export const ATTACHMENTS_PER_MESSAGE_MAX = 10;

export interface EmpConversationMember {
  partyId: string;
  name: string;
  photoUrl: string | null;
  /** Null until they open it. Drives unread counts and read receipts alike. */
  lastReadAt: string | null;
}

export interface EmpConversation {
  id: string;
  kind: EmpConversationKind;
  /** Groups only; a direct conversation is titled by the other person. */
  title: string | null;
  members: EmpConversationMember[];
  lastMessage: EmpMessagePreview | null;
  unreadCount: number;
  pinnedAt: string | null;
  archivedAt: string | null;
  mutedUntil: string | null;
  createdAt: string;
}

/** Enough of the latest message to draw a list row without fetching the thread. */
export interface EmpMessagePreview {
  id: string;
  senderPartyId: string;
  /** Empty for an attachment-only message; the UI says "Photo", "Voice message". */
  body: string;
  kind: EmpMessageKind;
  createdAt: string;
}

// ──────────────────────────── Messages ────────────────────────────

/** What a message is mostly made of, for previews and accessible labels. */
export const MESSAGE_KINDS = ["text", "image", "file", "voice", "post"] as const;
export type EmpMessageKind = (typeof MESSAGE_KINDS)[number];

export const ATTACHMENT_KINDS = ["image", "file", "voice"] as const;
export type EmpAttachmentKind = (typeof ATTACHMENT_KINDS)[number];

export interface EmpAttachment {
  id: string;
  kind: EmpAttachmentKind;
  /** The name we chose from sniffing the bytes, never the uploaded name. */
  name: string;
  contentType: string;
  byteSize: number;
  /** Voice only: how long it runs, so a bubble can say "0:14" before loading. */
  durationMs: number | null;
  /**
   * Voice only: peaks computed in the browser at record time, 0..100. Lets a
   * bubble draw itself without downloading the audio — on care-home wifi that
   * is the difference between a usable list and a stalled one.
   */
  waveform: number[] | null;
}

export interface EmpMessage {
  id: string;
  conversationId: string;
  senderPartyId: string;
  body: string;
  kind: EmpMessageKind;
  attachments: EmpAttachment[];
  /**
   * True when this message shares a post, whether or not the reader may see
   * it. Kept separate from `sharedPost` so the bubble can tell "there is a
   * post here you can't open" from "there is no post here" — the sender may
   * have typed a comment alongside it, which would otherwise make the two
   * indistinguishable.
   */
  hasSharedPost: boolean;
  /**
   * The shared post, resolved for THIS reader. Null when they may not read it:
   * outside its audience, or it has since been hidden, ended or deleted. The
   * bubble says so rather than rendering an empty card.
   */
  sharedPost: EmpSharedPost | null;
  editedAt: string | null;
  /** Set when withdrawn: the row stays so the thread keeps its shape. */
  deletedAt: string | null;
  createdAt: string;
}

/** A message the caller is still sending, reconciled by `clientId` on arrival. */
export interface EmpPendingMessage {
  clientId: string;
  body: string;
  createdAt: string;
}

// ──────────────────────────── Sending ────────────────────────────

export const VOICE_DURATION_MAX_MS = 2 * 60 * 1000;
export const WAVEFORM_MAX_POINTS = 100;

/**
 * Voice notes are MP3, encoded in the browser before upload.
 *
 * Not because MP3 is good, but because it is the one format every device
 * plays. `MediaRecorder` hands you `webm/opus` on Android and Chrome and
 * `mp4/aac` on iOS, and Safari will not reliably play the former — so a
 * carer's note recorded on an Android phone would simply not play for a
 * manager on an iPhone, intermittently, depending on who recorded it. That is
 * a miserable bug to field.
 *
 * Encoding client-side rather than transcoding server-side keeps ffmpeg out of
 * the container image entirely.
 */
export const VOICE_CONTENT_TYPE = "audio/mpeg";
export const VOICE_SAMPLE_RATE = 22_050;
export const VOICE_BITRATE_KBPS = 64;

/** Two minutes at 64kbps is about a megabyte; this leaves generous headroom. */
export const VOICE_BYTES_MAX = 4 * 1024 * 1024;

/**
 * Is this actually an MP3? Checked on the server against the bytes, because
 * the upload's claims about itself are worth nothing. An MP3 starts with
 * either an ID3 tag or a frame sync (11 set bits).
 */
export function looksLikeMp3(buf: Uint8Array): boolean {
  if (buf.length < 3) return false;
  if (buf[0] === 0x49 && buf[1] === 0x44 && buf[2] === 0x33) return true; // "ID3"
  return buf[0] === 0xff && (buf[1] & 0xe0) === 0xe0; // frame sync
}

/** A waveform the bubble can draw: 0..100, capped, and free of nonsense. */
export function cleanWaveform(points: unknown): number[] | null {
  if (!Array.isArray(points) || points.length === 0) return null;
  const clean = points
    .filter((n): n is number => typeof n === "number" && Number.isFinite(n))
    .slice(0, WAVEFORM_MAX_POINTS)
    .map((n) => Math.max(0, Math.min(100, Math.round(n))));
  return clean.length > 0 ? clean : null;
}

/**
 * Why messages can be edited for a while but not forever: a thread is a record
 * two people rely on, and silently rewriting yesterday's message changes what
 * the other person remembers agreeing to. An edit inside the window shows as
 * edited; after it, withdraw and send again.
 */
export const MESSAGE_EDIT_WINDOW_MS = 15 * 60 * 1000;

export function canEditMessage(
  message: Pick<EmpMessage, "senderPartyId" | "createdAt" | "deletedAt">,
  actorPartyId: string,
  now = Date.now(),
): boolean {
  if (message.senderPartyId !== actorPartyId) return false;
  if (message.deletedAt) return false;
  return now - new Date(message.createdAt).getTime() <= MESSAGE_EDIT_WINDOW_MS;
}

/** Withdrawing has no window: you can always take back something you sent. */
export function canDeleteMessage(
  message: Pick<EmpMessage, "senderPartyId" | "deletedAt">,
  actorPartyId: string,
): boolean {
  return message.senderPartyId === actorPartyId && !message.deletedAt;
}

/** What a message is "mostly", for the preview line and screen readers. */
export function messageKindOf(
  body: string,
  attachments: readonly Pick<EmpAttachment, "kind">[],
  hasSharedPost = false,
): EmpMessageKind {
  // Body first, as before: a share or an attachment sent with a comment reads
  // as what the sender wrote, because that is what a list row should preview.
  if (body.trim().length > 0) return "text";
  if (hasSharedPost) return "post";
  const first = attachments[0];
  if (!first) return "text";
  return first.kind;
}

/**
 * A message has to say something: empty text with no attachment is not a
 * message, and accepting one puts a blank bubble in a thread forever.
 */
export function isSendable(body: string, attachmentCount: number, hasSharedPost = false): boolean {
  return body.trim().length > 0 || attachmentCount > 0 || hasSharedPost;
}

export const sendMessageSchema = z
  .object({
    body: z.string().max(MESSAGE_BODY_MAX).default(""),
    /**
     * The client's own id for this send. Unique per conversation, so a retry
     * after a dropped response returns the message already stored instead of
     * posting it twice — the classic duplicate on a flaky connection.
     */
    clientId: z.string().uuid(),
    attachmentIds: z.array(z.string().uuid()).max(ATTACHMENTS_PER_MESSAGE_MAX).default([]),
    /**
     * Share a post into this conversation. Direct conversations only, and only
     * a post both people may read — the server decides both, and refuses
     * rather than sending a card the recipient would see as unavailable.
     */
    sharedPostId: z.string().uuid().optional(),
  })
  .refine((m) => isSendable(m.body, m.attachmentIds.length, m.sharedPostId != null), {
    message: "Write something, attach a file, or share a post.",
    path: ["body"],
  });

export type EmpSendMessageInput = z.infer<typeof sendMessageSchema>;

export const editMessageSchema = z.object({
  body: z.string().trim().min(1).max(MESSAGE_BODY_MAX),
});

// ──────────────────────────── Presence and typing ────────────────────────────

/**
 * Presence follows the Directory's visibility rule, not conversation
 * membership: an instance set to `own_location` has hidden other locations, and
 * an online dot for someone you cannot see in the Directory would leak exactly
 * what that setting hides.
 */
export interface EmpPresence {
  partyId: string;
  online: boolean;
  lastSeenAt: string | null;
}

/** Ephemeral: never stored, so there is nothing to retain, report or export. */
export interface EmpTyping {
  conversationId: string;
  partyId: string;
}

export const TYPING_THROTTLE_MS = 3000;
export const TYPING_EXPIRY_MS = 5000;

// ──────────────────────────── Reporting ────────────────────────────

export const MESSAGE_REPORT_REASONS = [
  "harassment",
  "inappropriate",
  "resident_information",
  "spam",
  "other",
] as const;
export type EmpMessageReportReason = (typeof MESSAGE_REPORT_REASONS)[number];

export const MESSAGE_REPORT_REASON_LABELS: Readonly<Record<EmpMessageReportReason, string>> = {
  harassment: "Harassment or bullying",
  inappropriate: "Inappropriate content",
  // Named explicitly because it is the one this organisation most needs told
  // about, and the one people are least likely to file under "other".
  resident_information: "Contains resident information",
  spam: "Spam",
  other: "Something else",
};

export const REPORT_DETAILS_MAX = 500;

/**
 * How long a reviewed report's evidence copy is kept.
 *
 * Deliberately longer than the 90-day message default: a report filed in the
 * last week of a message's life would otherwise lose its own evidence while an
 * HR process was still running, which is exactly when someone needs to look at
 * it. Deliberately not forever either — the copy outlives the conversation it
 * came from, so left alone it becomes a private archive nobody agreed to.
 *
 * Fixed rather than a tenant setting: it is the one retention a tenant should
 * not be able to shorten to hide a complaint, or extend without limit.
 */
export const REPORT_EVIDENCE_KEEP_DAYS = 180;

export const reportMessageSchema = z.object({
  reason: z.enum(MESSAGE_REPORT_REASONS),
  details: z.string().trim().max(REPORT_DETAILS_MAX).optional(),
});

/**
 * What an Admin reviewing a report sees: the one reported message, copied at
 * the moment it was filed, and never a way into the conversation around it.
 * There is deliberately no `conversationId` on this type — a reviewer who had
 * one would be a short step from a thread they are not a participant in.
 */
export interface EmpMessageReport {
  id: string;
  reason: EmpMessageReportReason;
  details: string | null;
  reporter: { partyId: string; name: string };
  /** The author of the reported message. */
  author: { partyId: string; name: string };
  /** The copy, not a reference: it outlives retention of the original. */
  body: string;
  attachments: EmpAttachment[];
  status: "open" | "actioned" | "dismissed";
  reportedAt: string;
}

// ──────────────────────────── Settings ────────────────────────────

export const MESSAGE_RETENTION_DAYS_DEFAULT = 90;
export const MESSAGE_RETENTION_DAYS_MIN = 7;
export const MESSAGE_RETENTION_DAYS_MAX = 3650;
export const MESSAGE_NOTICE_MAX = 1000;
export const ATTACHMENT_BYTES_MAX_DEFAULT = 25 * 1024 * 1024;

/**
 * The notice shown before someone's first message. Its wording is a go-live
 * blocker (roadmap decision 11): messaging cannot launch until the customer has
 * signed off both this text and the retention period, because it is what tells
 * staff not to put resident information in a private message.
 */
export interface EmpMessageSettings {
  retentionDays: number;
  notice: string | null;
  attachmentBytesMax: number;
  voiceDurationMaxMs: number;
}

export const DEFAULT_MESSAGE_SETTINGS: EmpMessageSettings = {
  retentionDays: MESSAGE_RETENTION_DAYS_DEFAULT,
  notice: null,
  attachmentBytesMax: ATTACHMENT_BYTES_MAX_DEFAULT,
  voiceDurationMaxMs: VOICE_DURATION_MAX_MS,
};

export const messageSettingsSchema = z.object({
  retentionDays: z.number().int().min(MESSAGE_RETENTION_DAYS_MIN).max(MESSAGE_RETENTION_DAYS_MAX),
  notice: z.string().trim().max(MESSAGE_NOTICE_MAX).nullable(),
});

/** A message is past its retention when it is older than the setting allows. */
export function isPastRetention(
  createdAt: string,
  retentionDays: number,
  now = Date.now(),
): boolean {
  return now - new Date(createdAt).getTime() > retentionDays * 24 * 60 * 60 * 1000;
}
